import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Button, Alert, Chip } from '@mui/material';
import Breadcrumb from '../components/Breadcrumb';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from '../api';
import { API_BASE } from '../config';
import { useToast } from '../components/ToastProvider';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { pageContainerSx } from '../styles/tokens';

const GCalSettings = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const showToast = useToast();

  const checkConnectionStatus = async () => {
    try {
      setCheckingStatus(true);
      const response = await axios.get('/api/google/status');
      setIsConnected(response.data.connected);
      if (response.data.connected) {
        showToast('Google Calendar is connected!', 'success');
      }
    } catch (error) {
      setIsConnected(false);
      console.error('Error checking connection status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const handleSyncAllProducts = async () => {
    try {
      setSyncStatus('syncing');
      const response = await axios.post('/api/google/sync_all_products');
      setSyncStatus('success');
      showToast(response.data.message, 'success');
    } catch (error) {
      setSyncStatus('error');
      if (error.response && error.response.status === 401) {
        showToast('Please connect to Google Calendar first by clicking "Connect Google Calendar"', 'warning');
      } else {
        showToast('Failed to sync products to Google Calendar', 'error');
      }
    }
  };

  if (checkingStatus) {
    return <PageLoader message="Loading calendar settings..." />;
  }

  return (
    <Box sx={{ ...pageContainerSx, mb: 4 }}>
      <PageHeader
        title="Calendar Settings"
        subtitle="Sync bookings with Google Calendar"
        actions={(
          <Chip
            label={isConnected ? 'Connected' : 'Not Connected'}
            color={isConnected ? 'success' : 'warning'}
            size="medium"
            sx={{ fontWeight: 600 }}
          />
        )}
      />
      <Breadcrumb items={[{ label: 'G-Calendar Settings', path: null }]} />
      <Card 
        elevation={0}
        sx={{ 
          p: { xs: 3, md: 4 }, 
          borderRadius: 3, 
          bgcolor: 'background.paper',
          width: '100%', 
          maxWidth: 1200,
          mx: 'auto',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >

        <Card 
          elevation={0}
          sx={{ 
            p: 3.5, 
            mb: 3, 
            borderRadius: 2, 
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CalendarTodayIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: '#424242' }}>
                Google Calendar Integration
              </Typography>
            </Box>
            {!checkingStatus && (
              <Chip
                label={isConnected ? 'Connected' : 'Not Connected'}
                color={isConnected ? 'success' : 'warning'}
                size="medium"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sync your bookings to Google Calendar. Only booking events (dispatch to return dates) are added—no availability events.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {!isConnected ? (
              <Button
                variant="contained"
                color="primary"
                href={`${API_BASE}/api/google/auth`}
                target="_blank"
                startIcon={<CalendarTodayIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                  }
                }}
              >
                Connect Google Calendar
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={checkConnectionStatus}
                startIcon={<CalendarTodayIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Refresh Connection
              </Button>
            )}

            <Button
              variant="outlined"
              color="success"
              onClick={handleSyncAllProducts}
              disabled={syncStatus === 'syncing' || !isConnected}
              startIcon={<CalendarTodayIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Bookings to Calendar'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant="text"
              color="info"
              onClick={checkConnectionStatus}
              disabled={checkingStatus}
              size="small"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {checkingStatus ? 'Checking...' : 'Check Status'}
            </Button>

            <Button
              variant="text"
              color="secondary"
              onClick={async () => {
                try {
                  const response = await axios.get('/api/google/debug-session');
                  console.log('Session Debug:', response.data);
                  showToast('Check browser console for session debug info', 'info');
                } catch (error) {
                  console.error('Debug error:', error);
                  showToast('Debug failed', 'error');
                }
              }}
              size="small"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Debug Session
            </Button>

            <Button
              variant="text"
              color="warning"
              onClick={async () => {
                try {
                  const response = await axios.post('/api/google/test-connection');
                  console.log('Test connection set:', response.data);
                  showToast('Test connection set - check status now', 'info');
                  checkConnectionStatus();
                } catch (error) {
                  console.error('Test connection error:', error);
                  showToast('Test connection failed', 'error');
                }
              }}
              size="small"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Test Connection
            </Button>

            <Button
              variant="text"
              color="error"
              onClick={async () => {
                try {
                  const response = await axios.post('/api/google/clear-credentials');
                  console.log('Credentials cleared:', response.data);
                  showToast('Credentials cleared - try connecting again', 'info');
                  checkConnectionStatus();
                } catch (error) {
                  console.error('Clear credentials error:', error);
                  showToast('Failed to clear credentials', 'error');
                }
              }}
              size="small"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Clear Credentials
            </Button>
          </Box>

          <Box 
            sx={{ 
              p: 2.5, 
              borderRadius: 2, 
              bgcolor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              {!isConnected ? (
                <>
                  💡 <strong>Step 1:</strong> Click &quot;Connect Google Calendar&quot; to authorize access.<br />
                  💡 <strong>Step 2:</strong> After connecting, click &quot;Sync Bookings to Calendar&quot; to add all your bookings (dispatch–return dates) to Google Calendar.
                </>
              ) : (
                <>
                  ✅ <strong>Connected!</strong> Click &quot;Sync Bookings to Calendar&quot; to add all booking events to your calendar. Only bookings are synced (no availability events).
                </>
              )}
            </Typography>
          </Box>

          {syncStatus === 'success' && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
              Sync completed successfully.
            </Alert>
          )}

          {syncStatus === 'error' && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              Failed to sync. Please make sure you&apos;re connected to Google Calendar first.
            </Alert>
          )}
        </Card>
      </Card>
    </Box>
  );
};

export default GCalSettings;

